/**
 * AMZEL Voice-AI Demo — Strategic Call Preview (Mai 2026).
 *
 * 4 Tab-artige Scenes:
 *   1. "A Normal Day"      — Channel-Load + Stress-Heatmap (Aggregate)
 *   2. "Talk to Your Cell" — Live Voice-AI roundtrip (Killer-Demo)
 *   3. "An Incident"       — Active-Threat Acoustic Detection
 *   4. "Architecture"      — SLR5500 -> Mini-PC -> Cloud Datenflow
 *
 * Komplett isoliert von der restlichen Nest-UI. Eigene Shell-Variante.
 * Inhaltlich nur generische Site-Labels (Site Alpha / Beta / Gamma).
 */
import { useState } from "react";
import { Activity, Mic, Shield, Workflow } from "lucide-react";

import { ShellDemo } from "./ShellDemo";
import { SceneNormalDay } from "./scenes/SceneNormalDay";
import { SceneTalkToCell } from "./scenes/SceneTalkToCell";
import { SceneIncident } from "./scenes/SceneIncident";
import { SceneArchitecture } from "./scenes/SceneArchitecture";

type SceneId = "normal-day" | "talk-to-cell" | "incident" | "architecture";

const SCENES: ReadonlyArray<{
  id: SceneId;
  label: string;
  sub: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}> = [
  { id: "normal-day", label: "A Normal Day", sub: "Channel-Load + Stress", icon: Activity },
  { id: "talk-to-cell", label: "Talk to Your Cell", sub: "Live Voice-AI", icon: Mic },
  { id: "incident", label: "An Incident", sub: "Acoustic Threat Detection", icon: Shield },
  { id: "architecture", label: "Architecture", sub: "How it works", icon: Workflow },
];

export default function VoiceAiDemo() {
  const [active, setActive] = useState<SceneId>("normal-day");

  return (
    <ShellDemo>
      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
          AMZEL · Voice-AI on the only sanctioned warehouse comms layer
        </p>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Talk to your radio cell. Your radio cell talks back.
        </h1>
        <p className="max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
          A live walkthrough of three concrete Voice-AI capabilities running on Spreenauten's
          edge-resilient DMR platform — including silent-mode active-threat response. Each scene
          uses real AI inference; only the radio audio is staged for demonstration.
        </p>
      </section>

      <nav className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SCENES.map((s) => {
          const isActive = active === s.id;
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              className={`rounded-2xl border p-4 text-left transition-all ${
                isActive
                  ? "border-emerald-400 bg-emerald-50 shadow-md dark:border-emerald-500/40 dark:bg-emerald-500/10"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
              }`}
            >
              <Icon
                className={`size-5 ${
                  isActive ? "text-emerald-700 dark:text-emerald-400" : "text-slate-400 dark:text-slate-500"
                }`}
                aria-hidden
              />
              <div className="mt-2 font-semibold tracking-tight">{s.label}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{s.sub}</div>
            </button>
          );
        })}
      </nav>

      <div className="mt-8">
        {active === "normal-day" && <SceneNormalDay />}
        {active === "talk-to-cell" && <SceneTalkToCell />}
        {active === "incident" && <SceneIncident />}
        {active === "architecture" && <SceneArchitecture />}
      </div>
    </ShellDemo>
  );
}
